import React, { FC, useEffect, useRef, useState } from "react"
import styled from "styled-components"

import { generateConditionalClassName, isClientSide } from "@utils"
import { Wistia } from "@helpscout/hs-marketing-shared-components"

const VIDEO_SLIDES = [
  {
    title: "Reply in seconds",
    wistiaId: "ezpdvnwon5",
  },
  {
    title: "Make it personal",
    wistiaId: "st0vtskd4g",
  },
  {
    title: "Prioritize with ease",
    wistiaId: "kn5t8x66mf",
  },
]

const MOBILE_NAV_BREAKPOINT = 708

const VideoSlideshowDIV = styled.div`
  --video-width: var(--container-width);

  display: flex;
  flex-direction: column;
  gap: var(--spacing--S);
  justify-content: center;

  .video-slideshow-tabs-wrapper {
    width: 100%;
    overflow-x: auto;
    mask-image: linear-gradient(
      to right,
      transparent 0,
      black calc((100% - var(--container-width)) / 2),
      black calc((100% - var(--container-width)) / 2 + var(--container-width)),
      transparent 100%
    );

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .video-slideshow-tabs {
    display: flex;
    gap: var(--spacing--S);
    justify-content: flex-start;
    margin: 0 auto;
    min-width: fit-content;
    padding: 0 calc((100% - var(--container-width)) / 2);

    &::-webkit-scrollbar {
      display: none;
    }

    @media (min-width: ${MOBILE_NAV_BREAKPOINT}px) {
      justify-content: center;
    }

    button {
      display: block;
      flex: 0 0 min(calc((var(--video-width) - var(--spacing--XS) * 2) / 3), calc(var(--spacing--L) * 3));
      margin-bottom: 0;
      padding: var(--spacing--base-1) 0;
      position: relative;
      text-align: center;
      transition: padding 0.3s;
      white-space: nowrap;

      @media (hover: hover) {
        &:hover {
          padding: var(--spacing--base-0-75) 0 var(--spacing--base-1-25);
        }
      }

      &::before,
      &::after {
        background: rgba(var(--color--charcoal-1200-rgb), 0.24);
        bottom: 0;
        content: "";
        display: block;
        height: 0;
        left: 0;
        position: absolute;
        transition: height 0.3s;
        width: 100%;
      }

      &::after {
        background: var(--color--charcoal-1200);
        width: var(--video-progress);
        transition: height 0.3s, width 1s linear;
      }
      &.is-active {
        &::before,
        &::after {
          height: 2px;
        }
      }

      @media (hover: hover) {
        &:hover {
          &::before,
          &::after {
            height: 2px;
          }
        }
      }
    }
  }

  .video-slideshow-videos-wrapper {
    padding: 0 calc((100% - var(--video-width)) / 2);
  }

  .video-slideshow-videos {
    display: flex;
    gap: var(--spacing--2XL);
    flex-wrap: nowrap;
    transition: translate 0.5s ease-in-out;

    .Wistia {
      background: var(--color--basics-white);
      box-shadow: var(--elevation--2XL);
      border-radius: var(--border-radius--M);
      overflow: hidden;
      flex: 0 0 100%;
      height: auto;
      pointer-events: none;
      transition:
        opacity 0.5s ease-in-out,
        transform 0.5s ease-in-out;
      width: 100%;

      .w-ui-container {
        opacity: 0 !important;
      }
    } 

    &.current-slide--0 {
      .Wistia.slide--1,
      .Wistia.slide--2 {
        opacity: 0 !important;
        transform: scale(0.9);
      }
    }

    &.current-slide--1 {
      .Wistia.slide--0,
      .Wistia.slide--2 {
        opacity: 0 !important;
        transform: scale(0.9);
      }
    }

    &.current-slide--2 {
      .Wistia.slide--0,
      .Wistia.slide--1 {
        opacity: 0 !important;
        transform: scale(0.9);
      }
    }
  }
`

const HomeInboxTourVideoSlideshow: FC<{ isReady: boolean }> = ({ isReady }) => {
  const [isWistiaReady, setIsWistiaReady] = useState<boolean>(false)
  const [currentSlide, setCurrentSlide] = useState<number>(0)
  const [videosProgress, setVideosProgress] = useState<number[]>([0, 0, 0])
  const elRef = useRef<HTMLDivElement>(null)
  const wistiaVideos = useRef<any[]>([])
  const scrollerRef = useRef<HTMLDivElement>(null)

  const setVideoProgress = (i: number, pct: number) => {
    const _videosProgress = [0, 0, 0]
    _videosProgress[i] = pct
    setVideosProgress(_videosProgress)
  }

  useEffect(() => {
    if (!isClientSide() || !isReady) return

    if (wistiaVideos.current.length === 0) {
      window._wq = window._wq || []
      VIDEO_SLIDES.forEach((video, i) => {
        window._wq.push({
          id: video.wistiaId,
          onReady: (wistiaVideo) => {
            wistiaVideos.current.push(wistiaVideo)

            wistiaVideo.bind("play", () => {
              setVideoProgress(i, 0)
            })

            wistiaVideo.bind("timechange", () => {
              const _videoProgress = wistiaVideo.time() / wistiaVideo.duration() * 100
              setVideoProgress(i, _videoProgress)
            })

            wistiaVideo.bind("end", () => {
              setVideoProgress(i, 100)
              setCurrentSlide(_currentSlide => _currentSlide === VIDEO_SLIDES.length - 1 ? 0 : _currentSlide + 1)
            })

            if (i === 0) wistiaVideo.play()

            setIsWistiaReady(true)
          }
        })
      })
    }
  }, [isReady])

  useEffect(() => {
    if (!isClientSide() || wistiaVideos.current.length === 0) return

    if (!wistiaVideos.current[currentSlide]) return

    wistiaVideos.current.forEach(wistiaVideo => {
      wistiaVideo.time(0)
      wistiaVideo.pause()
    })

    setVideosProgress([0, 0, 0])

    wistiaVideos.current[currentSlide].play()

    if (window.innerWidth < MOBILE_NAV_BREAKPOINT) {
      scrollerRef.current?.scroll({
        left: currentSlide < 2 ?
          0 :
          9999,
        behavior: "smooth",
      })
    }
  }, [currentSlide])

  return (
    <VideoSlideshowDIV className="video-slideshow" ref={elRef}>
      <div className="video-slideshow-tabs-wrapper" ref={scrollerRef}>
        <div className="video-slideshow-tabs">
          {VIDEO_SLIDES.map((slide, i) => (
            <button
              className={generateConditionalClassName({
                "type--headline-medium-L": true,
                "is-active": currentSlide === i,
              })}
              key={slide.title}
              onClick={() => setCurrentSlide(i)}
              style={{
                "--video-progress": `${videosProgress[i]}%`,
              }}
            >
              {slide.title}
            </button>
          ))}
        </div>
      </div>
      <div className="video-slideshow-videos-wrapper">
        <div
          className={generateConditionalClassName({
            "video-slideshow-videos": true,
            [`current-slide--${currentSlide}`]: true,
          })}
          style={{
            translate: currentSlide !== 0 ? `calc((100% + var(--spacing--2XL)) * -${currentSlide})` : "0"
          }}
        >
          {VIDEO_SLIDES.map((slide, i) => (
            <Wistia
              className={`slide--${i}`}
              id={slide.wistiaId}
              key={slide.wistiaId}
            />
          ))}
        </div>
      </div>
    </VideoSlideshowDIV>
  )
}

export default HomeInboxTourVideoSlideshow
